import { exactSessionExpPoint } from '@/core/session-exp-counter'
import { isGcwEstimate } from './gcw-exp-estimator'
import {
  expRequirementRange,
  intersectExpRequirementRanges,
  representativeExpRequirement,
  type ExpRequirementRange,
} from '@/core/exp-requirement-estimator'
import type {
  AdaptiveMetricRoi,
  ExpExactCandidate,
  ExpConfirmationFailureReason,
  ExpValue,
  GameStateSnapshot,
  MetricRecognitionFailureReason,
  OcrCompletionSnapshot,
  ValidatedObservation,
} from '@/domain/types'

export type SessionStartBaselineStatus =
  | 'ready'
  | 'stale'
  | 'exp-confirming'
  | 'exp-incomplete'
  | 'level-unconfirmed'
  | 'exp-conflict'
  | 'capture-unavailable'

export type SessionStartBaselineEvaluation =
  | {
    readonly status: 'ready'
    readonly baseline: GameStateSnapshot
    readonly sourceAt: number
  }
  | {
    readonly status: Exclude<SessionStartBaselineStatus, 'ready'>
    readonly baseline: null
    readonly sourceAt: number | null
  }

export type SessionStartIssueReason =
  | MetricRecognitionFailureReason
  | 'mixed-content'
  | 'confirming'
  | 'incomplete'
  | 'conflict'
  | 'slow'
  | 'unresponsive'
  | 'engine-error'
  | 'capture-unavailable'

export interface SessionStartIssue {
  readonly metric: 'level' | 'exp' | null
  readonly reason: SessionStartIssueReason
  readonly candidate?: number
  readonly rawText?: string
  readonly selectedText?: string
  readonly confidence?: number
  readonly completion?: OcrCompletionSnapshot
  readonly roi?: AdaptiveMetricRoi
  readonly recognitionPath?: 'legacy' | 'adaptive'
  readonly confirmedSamples?: 0 | 1
  readonly reviewFailureReason?: MetricRecognitionFailureReason
  readonly confirmationFailureReason?: ExpConfirmationFailureReason
  readonly requestedAt: number
}

export interface ConfirmedSessionStartExp<AllowUnknown extends boolean = false> {
  readonly level: AllowUnknown extends true ? number | null : number
  readonly value: ExpValue & { readonly absolute: number; readonly ratio: number }
  readonly confidence: number
  readonly sourceAt: number
  readonly levelSourceAt: number | null
  readonly sampleCount: 2
  readonly recognitionPath: 'legacy' | 'adaptive'
}

export interface SessionStartExpConsensusResult<AllowUnknown extends boolean = false> {
  readonly status: 'ignored' | 'pending' | 'ready'
  readonly sampleCount: 0 | 1 | 2
  readonly confirmed: ConfirmedSessionStartExp<AllowUnknown> | null
  readonly confirmationFailureReason?: ExpConfirmationFailureReason
}

/** Request-scoped confirmation; candidates never enter the accounting ledger. */
export class SessionStartExpConsensus<AllowUnknown extends boolean = false> {
  private samples: ExpExactCandidate[] = []
  private failureReason: ExpConfirmationFailureReason | undefined

  constructor(
    private readonly requestedAt: number,
    private readonly minimumConfidence: number,
    initialCandidate?: ExpExactCandidate,
    private readonly allowUnknownLevel: AllowUnknown = false as AllowUnknown,
  ) {
    if (initialCandidate && this.validCandidate(initialCandidate, true)) {
      if (expRequirementRange(initialCandidate.value, initialCandidate.percentagePrecision) === undefined) {
        this.failureReason = 'invalid-exp-range'
      } else {
        this.samples = [initialCandidate]
      }
    }
  }

  observe(candidate: ExpExactCandidate | undefined): SessionStartExpConsensusResult<AllowUnknown> {
    if (!candidate || !this.validCandidate(candidate, false)) return this.result('ignored')
    const previous = this.samples.at(-1)
    if (previous && candidate.sourceAt <= previous.sourceAt) return this.result('ignored')
    if (expRequirementRange(candidate.value, candidate.percentagePrecision) === undefined) {
      this.failureReason = 'invalid-exp-range'
      return this.result('ignored')
    }
    this.failureReason = undefined
    if (!previous || previous.value.sourceId !== candidate.value.sourceId
      || previous.level !== candidate.level && !(this.allowUnknownLevel && (previous.level === null || candidate.level === null))) {
      this.samples = [candidate]
      if (previous) this.failureReason = 'incompatible-exp-samples'
      return this.result('pending')
    }
    if (candidate.value.absolute < previous.value.absolute) {
      this.samples = [candidate]
      this.failureReason = 'incompatible-exp-samples'
      return this.result('pending')
    }

    const range = intersectCandidateRequirementRanges(previous, candidate)
    if (range === undefined) {
      this.samples = [candidate]
      this.failureReason = 'incompatible-exp-samples'
      return this.result('pending')
    }
    const requiredExp = range === null || range.maximum === Number.MAX_SAFE_INTEGER
      ? null
      : representativeExpRequirement(
          range,
          candidate.value.absolute,
          candidate.value.ratio > 0 ? candidate.value.absolute / candidate.value.ratio : null,
        )
    const confirmed: ConfirmedSessionStartExp<AllowUnknown> = {
      level: candidate.level as ConfirmedSessionStartExp<AllowUnknown>['level'],
      value: {
        ...candidate.value,
        absolute: candidate.value.absolute,
        ratio: candidate.value.ratio,
        requiredExp,
        requiredExpApproximate: requiredExp !== null,
        requiredExpRange: range,
        percentagePrecision: candidate.percentagePrecision,
      },
      confidence: Math.min(previous.confidence, candidate.confidence),
      sourceAt: candidate.sourceAt,
      levelSourceAt: candidate.levelSourceAt,
      sampleCount: 2,
      recognitionPath: candidate.recognitionPath,
    }
    this.samples = [previous, candidate]
    return { status: 'ready', sampleCount: 2, confirmed }
  }

  get sampleCount(): 0 | 1 | 2 {
    return Math.min(2, this.samples.length) as 0 | 1 | 2
  }

  get confirmationFailureReason(): ExpConfirmationFailureReason | undefined {
    return this.failureReason
  }

  private validCandidate(candidate: ExpExactCandidate, allowBeforeRequest: boolean): boolean {
    return (allowBeforeRequest || candidate.sourceAt >= this.requestedAt)
      && candidate.deliveredAt >= candidate.sourceAt
      && candidate.confidence >= this.minimumConfidence
      && (this.allowUnknownLevel && candidate.level === null || candidate.level !== null
        && Number.isInteger(candidate.level) && candidate.level >= 1 && candidate.level <= 200)
      && (!this.allowUnknownLevel || candidate.barConsistency !== 'conflict')
      && Number.isSafeInteger(candidate.value.absolute)
      && candidate.value.absolute >= 0
      && Number.isFinite(candidate.value.ratio)
      && candidate.value.ratio >= 0
      && candidate.value.ratio <= 1
      && Number.isInteger(candidate.percentagePrecision)
      && candidate.percentagePrecision >= 0
      && candidate.percentagePrecision <= 4
  }

  private result(status: 'ignored' | 'pending'): SessionStartExpConsensusResult<AllowUnknown> {
    return {
      status,
      sampleCount: this.sampleCount,
      confirmed: null,
      ...(this.failureReason ? { confirmationFailureReason: this.failureReason } : {}),
    }
  }
}

function intersectCandidateRequirementRanges(
  first: Pick<ExpExactCandidate, 'value' | 'percentagePrecision'>,
  second: Pick<ExpExactCandidate, 'value' | 'percentagePrecision'>,
): ExpRequirementRange | null | undefined {
  const left = expRequirementRange(first.value, first.percentagePrecision)
  const right = expRequirementRange(second.value, second.percentagePrecision)
  if (left === undefined || right === undefined) return undefined
  if (left === null && right === null) return null
  if (left === null) return right
  if (right === null) return left
  return intersectExpRequirementRanges([left, right])
}

export interface SessionStartBaselineOptions {
  readonly now: number
  readonly captureGeneration: number
  readonly observationGeneration: number
  readonly maximumAgeMs: number | null
  readonly minimumCapturedAt?: number
  readonly mesosMaximumAgeMs: number
}

export function evaluateSessionStartBaseline(
  state: GameStateSnapshot | null,
  observation: ValidatedObservation | null,
  options: SessionStartBaselineOptions,
): SessionStartBaselineEvaluation {
  if (options.observationGeneration !== options.captureGeneration) {
    return unavailable('capture-unavailable')
  }
  if (!state || !observation) return unavailable('stale')

  const levelSourceAt = observation.level.capturedAt
  if (observation.level.availability === 'unavailable'
    || observation.level.value === null
    || !observation.accepted.level
    || state.level === null
    || state.level !== observation.level.value
    || state.observedAt?.level !== levelSourceAt) {
    return unavailable('level-unconfirmed', levelSourceAt)
  }
  if (!isFreshSource(levelSourceAt, options)) return unavailable('stale', levelSourceAt)

  const sourceAt = observation.exp.capturedAt
  const exp = observation.exp.value
  const expDiagnostic = observation.diagnostics.metrics?.exp?.status
  if (observation.exp.availability === 'unavailable' || !exp) {
    return unavailable(expDiagnostic === 'confirming' ? 'exp-confirming' : 'exp-incomplete', sourceAt)
  }
  if ((exp.absolute === null && !exp.percentageOnly) || exp.ratio === null) {
    return unavailable(expDiagnostic === 'confirming' ? 'exp-confirming' : 'exp-incomplete', sourceAt)
  }
  if (!isFreshSource(sourceAt, options)) {
    return unavailable('stale', sourceAt)
  }
  const estimatedReady = (isGcwEstimate(exp) || exp.percentageOnly === true && exp.absolute === null && exp.ratio !== null) && observation.expEstimatedAdmission?.status === 'accepted'
    && observation.expEstimatedEvidence?.sourceAt === sourceAt
  if (exp.estimated && !estimatedReady) return unavailable('exp-incomplete', sourceAt)
  const candidateState = { ...state, level: observation.level.value, exp }
  if (!estimatedReady && !exactSessionExpPoint(candidateState)) return unavailable('exp-conflict', sourceAt)
  if (!observation.accepted.exp) {
    return unavailable('exp-confirming', sourceAt)
  }
  if (state.observedAt?.exp !== sourceAt
    || state.exp.absolute !== exp.absolute
    || state.exp.ratio !== exp.ratio) {
    return unavailable('exp-confirming', sourceAt)
  }
  if (!estimatedReady && !exactSessionExpPoint(state)) {
    return unavailable(state.exp.estimated ? 'exp-incomplete' : 'exp-conflict', sourceAt)
  }

  const mesosFresh = observation.accepted.mesos
    && observation.mesos.value !== null
    && state.mesos === observation.mesos.value
    && state.observedAt?.mesos === observation.mesos.capturedAt
    && options.now >= observation.mesos.capturedAt
    && options.now - observation.mesos.capturedAt <= options.mesosMaximumAgeMs

  return {
    status: 'ready',
    sourceAt,
    baseline: {
      ...state,
      mesos: mesosFresh ? state.mesos : null,
      observedAt: {
        ...state.observedAt,
        mesos: mesosFresh ? observation.mesos.capturedAt : null,
      },
    },
  }
}

function isFreshSource(sourceAt: number, options: SessionStartBaselineOptions): boolean {
  return Number.isFinite(sourceAt)
    && options.now >= sourceAt
    && (options.minimumCapturedAt === undefined || sourceAt >= options.minimumCapturedAt)
    && (options.maximumAgeMs === null || options.now - sourceAt <= options.maximumAgeMs)
}

function unavailable(
  status: Exclude<SessionStartBaselineStatus, 'ready'>,
  sourceAt: number | null = null,
): SessionStartBaselineEvaluation {
  return { status, baseline: null, sourceAt }
}
